import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _dec, _class, _descriptor, _dec2, _class2, _dec3, _dec4, _dec5, _dec6, _class3, _descriptor2, _descriptor3, _dec7, _dec8, _dec9, _dec10, _class4, _descriptor4, _descriptor5;
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { action, computed, observable, reaction } from 'mobx';
import { createTransformer } from 'mobx-utils';
import { task } from 'mobx-task';
import last from 'lodash/last';
import flatMap from 'lodash/flatMap';
import { extractMessageFromError } from '../utils/errorUtil';
import { translate as T } from '../utils/translate';

/**
 * State for the milestones view.
 */
var MilestonesViewState = (_dec7 = action.bound, _dec8 = action.bound, _dec9 = action.bound, _dec10 = action.bound, _class4 = /*#__PURE__*/function () {
  function MilestonesViewState(_ref4) {
    var _this3 = this;
    var milestoneStore = _ref4.milestoneStore,
      job = _ref4.job,
      sessionStore = _ref4.sessionStore,
      flashMessageStore = _ref4.flashMessageStore;
    _classCallCheck(this, MilestonesViewState);
    _initializerDefineProperty(this, "selectedMilestoneId", _descriptor4, this);
    _initializerDefineProperty(this, "showingMilestoneSwitcher", _descriptor5, this);
    this.milestoneStore = milestoneStore;
    this.sessionStore = sessionStore;
    this.flashMessageStore = flashMessageStore;
    this.job = job;
    var initial = getInitialMilestone(job);
    this.selectedMilestoneId = initial ? initial.id : null;
    this._createTaskLists();
    this.completionModal = new MilestoneCompletionModal({
      state: this
    });
    reaction(function () {
      return _this3.milestones.some(function (m) {
        return m.id === _this3.selectedMilestoneId;
      });
    }, function (included) {
      return !included && _this3.selectMilestone(_this3.nextMilestone || last(_this3.milestones));
    });
  }

  /**
   * Returns the currently selected milestone.
   * If none are selected (change in milestones for example), returns the
   * next milestone, or the last one if all milestones have been completed.
   */
  return _createClass(MilestonesViewState, [{
    key: "milestone",
    get: function get() {
      var _this4 = this;
      if (!this.selectedMilestoneId) {
        return this.nextMilestone || last(this.milestones);
      }
      return this.job.milestones.find(function (x) {
        return x.id === _this4.selectedMilestoneId;
      }) || this.nextMilestone || last(this.milestones);
    }

    /**
     * Returns the job member representing the current user for the job.
     * Returns `null` if the current user is not a collaborator (could be a HQ person).
     */
  }, {
    key: "currentJobMember",
    get: function get() {
      var currentMember = this.sessionStore.member;
      return this.job.members.find(function (x) {
        return x.user === currentMember;
      }) || null;
    }
  }, {
    key: "milestones",
    get: function get() {
      return this.job.milestones;
    }
  }, {
    key: "completedMilestones",
    get: function get() {
      return this.milestones.filter(function (x) {
        return x.completed;
      });
    }
  }, {
    key: "allTasksForCurrentMilestone",
    get: function get() {
      return flatMap(this.taskLists, function (x) {
        return x.tasks.map(function (x) {
          return x.task;
        });
      });
    }
  }, {
    key: "tasksCount",
    get: function get() {
      return this.allTasksForCurrentMilestone.length;
    }
  }, {
    key: "completedTasksCount",
    get: function get() {
      return this.allTasksForCurrentMilestone.filter(function (x) {
        return x.completed;
      }).length;
    }
  }, {
    key: "isProvider",
    get: function get() {
      var member = this.currentJobMember;
      return member ? member.type === 'PROVIDER' : false;
    }
  }, {
    key: "nextMilestone",
    get: function get() {
      return this.milestones.find(function (m) {
        return m.completed === false;
      });
    }
  }, {
    key: "previousMilestone",
    get: function get() {
      var index = Math.max(0, this.milestones.indexOf(this.milestone)) - 1;
      return this.milestones[index];
    }
  }, {
    key: "canCompleteMilestone",
    get: function get() {
      if (!this.nextMilestone) {
        return true;
      }
      return this.milestones.indexOf(this.milestone) <= this.milestones.indexOf(this.nextMilestone);
    }
  }, {
    key: "showError",
    value: function showError(err) {
      var message = extractMessageFromError(err);
      this.flashMessageStore.create({
        message: message,
        type: 'error'
      }).autoDismiss();
    }
  }, {
    key: "showMilestoneSwitcher",
    value: function showMilestoneSwitcher() {
      this.showingMilestoneSwitcher = true;
    }
  }, {
    key: "closeMilestoneSwitcher",
    value: function closeMilestoneSwitcher() {
      this.showingMilestoneSwitcher = false;
    }
  }, {
    key: "selectMilestone",
    value: function selectMilestone(milestone) {
      if (!milestone) {
        return;
      }
      this.selectedMilestoneId = milestone.id;
      this.closeMilestoneSwitcher();
    }

    /**
     * Creates the task lists.
     */
  }, {
    key: "_createTaskLists",
    value: function _createTaskLists() {
      var _this5 = this;
      var isProvider = this.isProvider;
      var teamMembers = this.job.teamIds.map(function (t) {
        return _this5.sessionStore.teamMembers.find(function (x) {
          return x.teamId === t;
        });
      }).filter(Boolean);
      var isTeamCollab = (teamMembers || []).find(function (x) {
        return x.hasPermission('UPDATE_TEAM_JOBS');
      });

      // Collaborator = somebody working on the job
      var isCollaborator = this.currentJobMember || isTeamCollab;
      this.taskLists = [new TaskList({
        title: isCollaborator ? 'My Tasks' : T('Web.Common.Client', 'Client') + ' Tasks',
        state: this,
        designee: isProvider ? 'Provider' : 'Client',
        isDesignee: isCollaborator,
        expanded: true
      }), new TaskList({
        title: isCollaborator ? isProvider ? T('Web.Common.Client', 'Client') + ' Tasks' : T('Web.Common.Provider', 'Provider') + ' Tasks' : T('Web.Common.Provider', 'Provider') + ' Tasks',
        state: this,
        designee: isProvider ? 'Client' : 'Provider',
        isDesignee: false,
        // If you are not on the job, then you are likely viewing from HQ, so
        // expand this tasklist as well.
        expanded: !isCollaborator
      })];
    }
  }]);
}(), _descriptor4 = _applyDecoratedDescriptor(_class4.prototype, "selectedMilestoneId", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function initializer() {
    return null;
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class4.prototype, "showingMilestoneSwitcher", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function initializer() {
    return false;
  }
}), _applyDecoratedDescriptor(_class4.prototype, "milestone", [computed], Object.getOwnPropertyDescriptor(_class4.prototype, "milestone"), _class4.prototype), _applyDecoratedDescriptor(_class4.prototype, "currentJobMember", [computed], Object.getOwnPropertyDescriptor(_class4.prototype, "currentJobMember"), _class4.prototype), _applyDecoratedDescriptor(_class4.prototype, "milestones", [computed], Object.getOwnPropertyDescriptor(_class4.prototype, "milestones"), _class4.prototype), _applyDecoratedDescriptor(_class4.prototype, "completedMilestones", [computed], Object.getOwnPropertyDescriptor(_class4.prototype, "completedMilestones"), _class4.prototype), _applyDecoratedDescriptor(_class4.prototype, "allTasksForCurrentMilestone", [computed], Object.getOwnPropertyDescriptor(_class4.prototype, "allTasksForCurrentMilestone"), _class4.prototype), _applyDecoratedDescriptor(_class4.prototype, "tasksCount", [computed], Object.getOwnPropertyDescriptor(_class4.prototype, "tasksCount"), _class4.prototype), _applyDecoratedDescriptor(_class4.prototype, "completedTasksCount", [computed], Object.getOwnPropertyDescriptor(_class4.prototype, "completedTasksCount"), _class4.prototype), _applyDecoratedDescriptor(_class4.prototype, "isProvider", [computed], Object.getOwnPropertyDescriptor(_class4.prototype, "isProvider"), _class4.prototype), _applyDecoratedDescriptor(_class4.prototype, "nextMilestone", [computed], Object.getOwnPropertyDescriptor(_class4.prototype, "nextMilestone"), _class4.prototype), _applyDecoratedDescriptor(_class4.prototype, "previousMilestone", [computed], Object.getOwnPropertyDescriptor(_class4.prototype, "previousMilestone"), _class4.prototype), _applyDecoratedDescriptor(_class4.prototype, "canCompleteMilestone", [computed], Object.getOwnPropertyDescriptor(_class4.prototype, "canCompleteMilestone"), _class4.prototype), _applyDecoratedDescriptor(_class4.prototype, "showError", [_dec7], Object.getOwnPropertyDescriptor(_class4.prototype, "showError"), _class4.prototype), _applyDecoratedDescriptor(_class4.prototype, "showMilestoneSwitcher", [_dec8], Object.getOwnPropertyDescriptor(_class4.prototype, "showMilestoneSwitcher"), _class4.prototype), _applyDecoratedDescriptor(_class4.prototype, "closeMilestoneSwitcher", [_dec9], Object.getOwnPropertyDescriptor(_class4.prototype, "closeMilestoneSwitcher"), _class4.prototype), _applyDecoratedDescriptor(_class4.prototype, "selectMilestone", [_dec10], Object.getOwnPropertyDescriptor(_class4.prototype, "selectMilestone"), _class4.prototype), _class4);
export { MilestonesViewState as default };
/**
 * Task list state.
 */
var TaskList = (_dec = action.bound, _class = /*#__PURE__*/function () {
  function TaskList(_ref) {
    var _this = this;
    var title = _ref.title,
      designee = _ref.designee,
      expanded = _ref.expanded,
      isDesignee = _ref.isDesignee,
      state = _ref.state;
    _classCallCheck(this, TaskList);
    _initializerDefineProperty(this, "expanded", _descriptor, this);
    this.transform = createTransformer(function (t) {
      return new TaskListItem({
        list: _this,
        task: t,
        state: _this.state,
        isDesignee: _this.isDesignee
      });
    });
    this.title = title;
    this.designee = designee;
    this.expanded = expanded;
    this.isDesignee = isDesignee;
    this.state = state;
  }
  return _createClass(TaskList, [{
    key: "milestone",
    get: function get() {
      return this.state.milestone;
    }
  }, {
    key: "tasks",
    get: function get() {
      if (!this.milestone) {
        return [];
      }
      var designee = this.designee;
      return this.milestone.tasks.filter(function (x) {
        return x.designee === designee;
      }).map(this.transform);
    }
  }, {
    key: "toggleExpansion",
    value: function toggleExpansion() {
      this.expanded = !this.expanded;
    }
  }]);
}(), _descriptor = _applyDecoratedDescriptor(_class.prototype, "expanded", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function initializer() {
    return false;
  }
}), _applyDecoratedDescriptor(_class.prototype, "milestone", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "milestone"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "tasks", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "tasks"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "toggleExpansion", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "toggleExpansion"), _class.prototype), _class);
/**
 * Task list item state.
 */
var TaskListItem = (_dec2 = task.resolved, _class2 = /*#__PURE__*/function () {
  function TaskListItem(_ref2) {
    var task = _ref2.task,
      state = _ref2.state,
      list = _ref2.list,
      isDesignee = _ref2.isDesignee;
    _classCallCheck(this, TaskListItem);
    this.task = task;
    this.state = state;
    this.list = list;
    this.isDesignee = isDesignee;
    this.toggleCompletion = this.toggleCompletion.bind(this);
  }
  return _createClass(TaskListItem, [{
    key: "canComplete",
    get: function get() {
      return this.isDesignee && this.state.canCompleteMilestone && this.state.job.status !== 'CLOSED';
    }
  }, {
    key: "isNextIncompleteTask",
    get: function get() {
      var nextTask = this.list.tasks.find(function (x) {
        return !x.task.completed;
      });
      return this === nextTask;
    }
  }, {
    key: "isLastInList",
    get: function get() {
      return this.list.tasks.indexOf(this) === this.list.tasks.length - 1;
    }
  }, {
    key: "toggleCompletion",
    value: function () {
      var _toggleCompletion = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var _this2 = this;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              if (this.isDesignee) {
                _context.next = 2;
                break;
              }
              return _context.abrupt("return");
            case 2:
              return _context.abrupt("return", (this.task.completed ? this.state.milestoneStore.uncomplete(this.task) : this.state.milestoneStore.complete(this.task)).catch(function (err) {
                _this2.state.showError(err);
                throw err;
              }));
            case 3:
            case "end":
              return _context.stop();
          }
        }, _callee, this);
      }));
      function toggleCompletion() {
        return _toggleCompletion.apply(this, arguments);
      }
      return toggleCompletion;
    }()
  }]);
}(), _applyDecoratedDescriptor(_class2.prototype, "canComplete", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "canComplete"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "isNextIncompleteTask", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "isNextIncompleteTask"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "isLastInList", [computed], Object.getOwnPropertyDescriptor(_class2.prototype, "isLastInList"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "toggleCompletion", [_dec2], Object.getOwnPropertyDescriptor(_class2.prototype, "toggleCompletion"), _class2.prototype), _class2);
/**
 * State for the completion modal.
 */
var MilestoneCompletionModal = (_dec3 = action.bound, _dec4 = action.bound, _dec5 = action.bound, _dec6 = action.bound, _class3 = /*#__PURE__*/function () {
  function MilestoneCompletionModal(_ref3) {
    var state = _ref3.state;
    _classCallCheck(this, MilestoneCompletionModal);
    _initializerDefineProperty(this, "showing", _descriptor2, this);
    _initializerDefineProperty(this, "milestone", _descriptor3, this);
    this.state = state;
    this.deactivate = onMilestoneCompleted(state, this.onMilestoneCompleted);
  }
  return _createClass(MilestoneCompletionModal, [{
    key: "allMilestonesCompleted",
    get: function get() {
      return this.state.milestones.every(function (x) {
        return x.completed;
      });
    }
  }, {
    key: "show",
    value: function show(milestone) {
      this.showing = true;
      this.milestone = milestone;
    }
  }, {
    key: "hide",
    value: function hide() {
      this.showing = false;
    }
  }, {
    key: "proceed",
    value: function proceed() {
      this.hide();
      if (this.state.nextMilestone) {
        this.state.selectMilestone(this.state.nextMilestone);
      }
    }

    /**
     * Triggered when a new milestone is seen as completed. Only once per milestone (*does not persist between reloads!*)
     *
     * @param {*} milestone
     */
  }, {
    key: "onMilestoneCompleted",
    value: function onMilestoneCompleted(milestone) {
      // Only show for people on the job.
      if (this.state.currentJobMember) {
        this.show(milestone);
      }
    }
  }]);
}(), _descriptor2 = _applyDecoratedDescriptor(_class3.prototype, "showing", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function initializer() {
    return false;
  }
}), _descriptor3 = _applyDecoratedDescriptor(_class3.prototype, "milestone", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function initializer() {
    return null;
  }
}), _applyDecoratedDescriptor(_class3.prototype, "allMilestonesCompleted", [computed], Object.getOwnPropertyDescriptor(_class3.prototype, "allMilestonesCompleted"), _class3.prototype), _applyDecoratedDescriptor(_class3.prototype, "show", [_dec3], Object.getOwnPropertyDescriptor(_class3.prototype, "show"), _class3.prototype), _applyDecoratedDescriptor(_class3.prototype, "hide", [_dec4], Object.getOwnPropertyDescriptor(_class3.prototype, "hide"), _class3.prototype), _applyDecoratedDescriptor(_class3.prototype, "proceed", [_dec5], Object.getOwnPropertyDescriptor(_class3.prototype, "proceed"), _class3.prototype), _applyDecoratedDescriptor(_class3.prototype, "onMilestoneCompleted", [_dec6], Object.getOwnPropertyDescriptor(_class3.prototype, "onMilestoneCompleted"), _class3.prototype), _class3);
/**
 * Calls `callback` when a milestone is observed to have been just completed.
 *
 * @param {*} state
 * @param {*} callback
 * @returns disposer function
 */
function onMilestoneCompleted(state, callback) {
  var getLastCompletedMilestoneId = function getLastCompletedMilestoneId() {
    var found = state.milestones.slice().reverse().find(function (x) {
      return x.completed;
    });
    return found ? found.id : null;
  };
  var lastCompletedMilestoneId = getLastCompletedMilestoneId();
  return reaction(function () {
    var milestoneIds = state.milestones.map(function (x) {
      return x.id;
    });
    // This function returns a Milestone that was just completed during this run.
    var nextId = getLastCompletedMilestoneId();
    if (!nextId) {
      return null;
    }
    if (nextId === lastCompletedMilestoneId) {
      return null;
    }
    var ret = milestoneIds.indexOf(lastCompletedMilestoneId) < milestoneIds.indexOf(nextId) ? state.milestones.find(function (x) {
      return x.id === nextId;
    }) : null;
    if (ret) {
      lastCompletedMilestoneId = nextId;
    }
    return ret;
  }, function (milestone) {
    return milestone && callback(milestone);
  });
}

/**
 * Gets the initial milestone to show.
 * @param {*} job
 */
function getInitialMilestone(job) {
  return job.milestones.find(function (x) {
    return x.completed === false;
  }) || last(job.milestones);
}
//# sourceMappingURL=MilestonesViewState.js.map
